// Ported from ~/Desktop/rondo/src/components/ui/Button.tsx.
// Adaptations: "use client" stripped; @/lib/color → ../../lib/color.

import { forwardRef, type ReactNode } from 'react'
import { motion, type HTMLMotionProps } from 'motion/react'
import { contrastText } from '../../lib/color.js'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'link' | 'accent'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  children?: ReactNode
  /** Background hex for `variant="accent"`. Text color picked automatically. */
  accentColor?: string
}

const CHROME_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-black',
  secondary: 'border border-black/15',
  ghost: 'bg-black/5',
  link: 'bg-black/5',
  accent: '',
}

const TEXT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'text-white',
  secondary: 'text-black',
  ghost: 'text-black',
  link: 'text-grey',
  accent: '',
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-label',
  md: 'h-10 px-4 text-body',
  lg: 'h-12 px-5 text-body',
  xl: 'h-14 px-6 text-body',
}

const LINK_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'text-label',
  md: 'text-body',
  lg: 'text-body',
  xl: 'text-body',
}

const ICON_ONLY_SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-14 w-14',
}

const CONTENT_GAP: Record<ButtonSize, string> = {
  sm: 'gap-1.5',
  md: 'gap-2',
  lg: 'gap-2',
  xl: 'gap-2',
}

const SPINNER_SIZE: Record<ButtonSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
  xl: 18,
}

const BASE =
  'relative inline-flex items-center justify-center leading-none font-semibold whitespace-nowrap select-none ' +
  'disabled:opacity-50 disabled:pointer-events-none'

const HOVER_SCALE = 1.03
const TAP_SCALE = 0.97

const solidChromeVariants = {
  rest: { scale: 1, opacity: 1 },
  hover: { scale: HOVER_SCALE, opacity: 1 },
  tap: { scale: TAP_SCALE, opacity: 1 },
}

const fadeChromeVariants = {
  rest: { scale: 0.94, opacity: 0 },
  hover: { scale: HOVER_SCALE, opacity: 1 },
  tap: { scale: TAP_SCALE, opacity: 1 },
}

const MOTION_TRANSITION = { type: 'spring' as const, stiffness: 400, damping: 25 }

function Spinner({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    iconLeft,
    iconRight,
    className,
    children,
    type = 'button',
    accentColor,
    style,
    ...motionProps
  },
  ref,
) {
  const isIconOnly = children == null && (iconLeft != null || iconRight != null || loading)
  const isInactive = disabled || loading
  const isFaint = variant === 'ghost' || variant === 'link'
  const isAccent = variant === 'accent'

  const sizing = isIconOnly
    ? ICON_ONLY_SIZE[size]
    : variant === 'link'
      ? LINK_SIZE_CLASSES[size]
      : SIZE_CLASSES[size]

  const accentTextColor = isAccent && accentColor ? contrastText(accentColor) : undefined

  const classes = [BASE, sizing, TEXT_CLASSES[variant], className].filter(Boolean).join(' ')

  const chromeBase =
    variant === 'link'
      ? 'absolute rounded-full inset-x-[-6px] inset-y-[-2px]'
      : 'absolute inset-0 rounded-full'
  const chromeClasses = `${chromeBase} ${CHROME_CLASSES[variant]}`
  const chromeVariants = isFaint ? fadeChromeVariants : solidChromeVariants
  const chromeStyle = isAccent && accentColor ? { backgroundColor: accentColor } : undefined

  const leadIcon = loading ? <Spinner size={SPINNER_SIZE[size]} /> : iconLeft

  const rootStyle =
    accentTextColor || style
      ? { ...(accentTextColor ? { color: accentTextColor } : null), ...style }
      : undefined

  return (
    <motion.button
      ref={ref}
      type={type}
      disabled={isInactive}
      aria-busy={loading || undefined}
      initial="rest"
      animate="rest"
      whileHover={isInactive ? undefined : 'hover'}
      whileTap={isInactive ? undefined : 'tap'}
      className={classes}
      style={rootStyle}
      {...motionProps}
    >
      <motion.span
        aria-hidden
        variants={chromeVariants}
        transition={MOTION_TRANSITION}
        className={chromeClasses}
        style={chromeStyle}
      />
      <span
        className={`relative inline-flex items-center justify-center ${CONTENT_GAP[size]}`}
        style={isIconOnly ? undefined : { transform: 'translateY(0.5px)' }}
      >
        {leadIcon}
        {children}
        {iconRight}
      </span>
    </motion.button>
  )
})
